import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { View as RNView } from 'react-native';

import { DraftFooter, DraftFooterButton } from './DraftFooter';
import { ReplyDraftHeader, useHeaderOverlayHeight } from './ReplyDraftHeader';
import { REPLY_DRAFT_STEPS } from './steps';
import { useDraftEditing } from '../../hooks/useDraftEditing';
import { useDraftHandOff } from '../../hooks/useDraftHandOff';
import { useHandOffToComposer } from '../../hooks/useHandOffToComposer';
import type { ComposerAccess } from '../../model/composer';
import type { ReplyDraftError } from '../../model/errors';
import {
  deriveStep,
  nextStep,
  previousStep,
  WIZARD_STEP_ORDER,
  type WizardStepKey,
} from '../../model/step';
import { REPLY_DRAFT_TOUR_ZONES } from '../../model/tour';
import type { DraftSelection, MustFixAcknowledgement } from '../../model/types';
import { MustFixList } from '../check/MustFixList';
import { REPLY_DRAFT_COPY } from '../copy';
import { CaseUpdatedNotice } from '../decide/CaseNotices';
import { DecideStep } from '../decide/DecideStep';
import { ReplaceConfirm } from '../handoff/ReplaceConfirm';
import { UseDraftFooter } from '../handoff/UseDraftFooter';
import { DraftPoints } from '../points/DraftPoints';
import { EdgeFade } from '../shared/EdgeFade';
import { SourceViewerContext, type SourceRef } from '../shared/SourceViewer';
import { announce } from '../shared/announce';
import { focusHeading, type HeadingNode } from '../shared/focusHeading';
import { SourceDetail } from '../source/SourceDetail';
import { ReplyDraftTourProvider, ReplyDraftTourReplay } from '../tour/ReplyDraftTour';
import { ReplyDraftTourZone } from '../tour/ReplyDraftTourZone';

import { Button, ButtonText } from '@/components/ui/button';
import { Center } from '@/components/ui/center';
import { ScrollView } from '@/components/ui/scroll-view';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { WizardSteps } from '@/components/wizard/WizardSteps';
import { useToaster } from '@/hooks/useToaster';
import {
  trackReplyDraftClosed,
  trackReplyDraftConflicted,
  trackReplyDraftOpened,
  trackReplyDraftReplaceDeclined,
  trackReplyDraftSourceViewed,
  trackReplyDraftStepViewed,
} from '@/util/analyticsEvents';

export type ReplyDraftWizardProps = {
  replyDraftId: string;
  conversationId: string;
  onClose: () => void;
  /** The conversation's composer, which a successful "use draft" fills. */
  composer: ComposerAccess;
  /**
   * Space under the draft, such as the phone's keyboard and home indicator
   * spacer. It renders inside the tour's container so the tour's scrim covers
   * it too, rather than leaving an undimmed strip at the foot of the screen.
   */
  bottomSpace?: ReactNode;
};

function unusableMessage(error: ReplyDraftError): string {
  return (
    REPLY_DRAFT_COPY.unusable[error.kind as keyof typeof REPLY_DRAFT_COPY.unusable] ??
    REPLY_DRAFT_COPY.loadFailed
  );
}

/** How far the header's fade reaches into the body before the first line. */
const HEAD_FADE_CLEARANCE = 14;

/** How far the footer's fade holds the fill above its button, where its label and padding sit. */
const FOOT_FADE_TAIL = 32;

/** Room under the last line of the body, past the footer. */
const BODY_END_SPACE = 24;

/** Room left between a tour target scrolled into sight and the header above it. */
const TOUR_TARGET_MARGIN = 16;

/**
 * The reply draft as a wizard, the way the reference assistant walks an
 * expert through adding references: choose a position, then add and remove
 * the reply's points, then use it. A row of steps shows where the expert is
 * and what is left, the title names the step, and every step moves on from
 * the same place in its footer, with Back beside it. The steps come from one
 * ordered list, so a later step such as picking references slots in there.
 *
 * The position comes first and alone. Until one is chosen only the case and
 * the options show, so no draft text can anchor the decision; that order is
 * part of the safety design, not a layout choice. Going back to it marks the
 * current option, and keeping it returns to the reply as it was left.
 *
 * Steps change without the reference assistant's entrance animation: a fade
 * between this draft's steps once stayed at zero opacity on iOS and left the
 * draft blank, which costs far more than the missing fade.
 *
 * The body scrolls the full height, and the footer floats over it on a fade,
 * so the draft passes out of sight under the controls rather than stopping
 * at a solid bar. A source opened from a point or an update takes the body's
 * place, with a way back in the footer. The points stay mounted underneath,
 * hidden, so a note half typed is as it was left, and the scroll returns to
 * where the link was.
 *
 * The expert's own point is held here rather than in its field. "Write my reply"
 * adds a point still sitting in the field before checking the reply, so a
 * sentence typed without pressing "Add point" is never dropped from it.
 *
 * Shared by the native full-screen route and the web modal, which differ only
 * in their header: the system bar on a phone, a floating title on web. The
 * first-draft tour wraps it here for the same reason, so both get it.
 */
export function ReplyDraftWizard({
  replyDraftId,
  conversationId,
  onClose,
  composer,
  bottomSpace,
}: ReplyDraftWizardProps) {
  const toast = useToaster();

  const headerOverlayHeight = useHeaderOverlayHeight();

  const [requested, setRequested] = useState<WizardStepKey>('review');

  const headingRef = useRef<HeadingNode>(null);

  const titleRef = useRef<HeadingNode>(null);

  const scrollRef = useRef<React.ComponentRef<typeof ScrollView>>(null);

  const [footerHeight, setFooterHeight] = useState(0);

  const [viewingSource, setViewingSource] = useState<SourceRef | null>(null);

  const [noteText, setNoteText] = useState('');

  const scrollOffset = useRef(0);

  const offsetBeforeSource = useRef(0);

  const onConflict = () => {
    trackReplyDraftConflicted({ replyDraftId, conversationId });

    toast.showInfo({ title: REPLY_DRAFT_COPY.conflict, liveRegion: 'polite' });
  };

  const onUnusable = (error: ReplyDraftError) => {
    toast.showWarning({ title: unusableMessage(error) });

    onClose();
  };

  const editing = useDraftEditing(replyDraftId, {
    onConflict,
    onFailed: () => toast.showWarning({ title: REPLY_DRAFT_COPY.saveFailed }),
    onUnusable,
  });

  const handOff = useHandOffToComposer(conversationId, composer);

  const finish = () => {
    onClose();

    composer.onHandedOff();
  };

  const handOffRequest = useDraftHandOff(replyDraftId, {
    flush: editing.flush,
    onHandedOff: (composedText) => {
      if (handOff.place(replyDraftId, composedText) === 'placed') {
        finish();
      }
    },
    onConflict,
    onUnusable,
  });

  const addNote = async (text: string) => {
    const isAdded = await editing.addNote(text);

    if (isAdded) {
      setNoteText('');
    }

    return isAdded;
  };

  const isAddingNoteBeforeHandOffRef = useRef(false);

  const [isAddingNoteBeforeHandOff, setIsAddingNoteBeforeHandOff] = useState(false);

  /**
   * Adds a point still sitting in its field, then hands off. The guard is set
   * before the first await: adding the note flushes pending taps first, and
   * the hand-off's own in-flight guard is not set until it starts, so without
   * this a second tap in that gap would add the note again or conflict.
   */
  const handOffWithNote = async () => {
    if (isAddingNoteBeforeHandOffRef.current) {
      return;
    }

    const pendingNote = noteText.trim();

    if (pendingNote) {
      isAddingNoteBeforeHandOffRef.current = true;

      setIsAddingNoteBeforeHandOff(true);

      try {
        if (!(await addNote(pendingNote))) {
          return;
        }
      } finally {
        isAddingNoteBeforeHandOffRef.current = false;

        setIsAddingNoteBeforeHandOff(false);
      }
    }

    await handOffRequest.submit();
  };

  const continueAfterMustFix = (acknowledgement: MustFixAcknowledgement) => {
    handOffRequest.submit(acknowledgement).catch(() => undefined);
  };

  const { draft, query } = editing;

  const step = deriveStep(draft, requested);

  const stepNumber = step === 'loading' ? 0 : WIZARD_STEP_ORDER.indexOf(step) + 1;

  const stepTitle = step === 'loading' ? REPLY_DRAFT_COPY.title : REPLY_DRAFT_COPY.stepTitles[step];

  const stepAnnouncement =
    step === 'loading'
      ? ''
      : REPLY_DRAFT_COPY.stepAnnouncement(stepNumber, REPLY_DRAFT_STEPS.length, stepTitle);

  const hasEnteredStep = useRef(false);

  /**
   * A new step starts at its top, with the reader on its heading and told
   * which step it is. The scroll view is shared by every step, so without the
   * scroll the next step opens wherever the last one was scrolled to. The
   * first step is not announced: the screen opening already says where the
   * expert is.
   */
  useEffect(() => {
    if (step === 'loading') {
      return;
    }

    scrollRef.current?.scrollTo?.({ y: 0, animated: false });

    focusHeading(headingRef.current);

    trackReplyDraftStepViewed({ replyDraftId, conversationId, step });

    if (hasEnteredStep.current) {
      announce(stepAnnouncement);
    }

    hasEnteredStep.current = true;
  }, [step, stepAnnouncement, replyDraftId, conversationId]);

  const isHeldByMustFix = handOffRequest.phase.kind === 'must_fix';

  useEffect(() => {
    if (isHeldByMustFix) {
      scrollRef.current?.scrollTo?.({ y: 0, animated: false });

      focusHeading(headingRef.current);
    }
  }, [isHeldByMustFix]);

  const viewSource = (source: SourceRef) => {
    offsetBeforeSource.current = scrollOffset.current;

    setViewingSource(source);

    trackReplyDraftSourceViewed({
      replyDraftId,
      conversationId,
      publicationId: source.publicationId,
      from: source.from,
    });
  };

  const hasViewedSource = useRef(false);

  useEffect(() => {
    if (viewingSource) {
      hasViewedSource.current = true;

      scrollRef.current?.scrollTo?.({ y: 0, animated: false });

      focusHeading(titleRef.current);

      return;
    }

    if (!hasViewedSource.current) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo?.({ y: offsetBeforeSource.current, animated: false });

      focusHeading(titleRef.current);
    });

    return () => cancelAnimationFrame(frame);
  }, [viewingSource]);

  const openedStatus = draft?.status;

  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (hasTrackedOpen.current || !openedStatus || step === 'loading') {
      return;
    }

    hasTrackedOpen.current = true;

    trackReplyDraftOpened({ replyDraftId, conversationId, status: openedStatus, step });
  }, [openedStatus, step, replyDraftId, conversationId]);

  const select = async (selection: DraftSelection) => {
    const isSelected = await editing.select(selection);

    if (isSelected) {
      setRequested('review');
    }

    return isSelected;
  };

  /**
   * Scrolls the body so a tour target sits clear of the floating header and
   * footer, and leaves it where it is when it already does. The tour measures
   * its target in the next frames, so it resolves only once the scroll has
   * been drawn.
   */
  const revealTourTarget = (node: RNView) =>
    new Promise<void>((resolve) => {
      const scrollNode = scrollRef.current as unknown as RNView | null;

      if (!scrollNode?.measureInWindow) {
        resolve();

        return;
      }

      scrollNode.measureInWindow((_scrollX, scrollY, _scrollWidth, scrollHeight) => {
        node.measureInWindow((_x, nodeY, _width, nodeHeight) => {
          const visibleTop = scrollY + headerOverlayHeight + TOUR_TARGET_MARGIN;

          const visibleBottom = scrollY + scrollHeight - footerHeight - TOUR_TARGET_MARGIN;

          if (nodeY >= visibleTop && nodeY + nodeHeight <= visibleBottom) {
            resolve();

            return;
          }

          scrollRef.current?.scrollTo?.({
            y: Math.max(0, scrollOffset.current + nodeY - visibleTop),
            animated: false,
          });

          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
      });
    });

  const close = () => {
    trackReplyDraftClosed({ replyDraftId, conversationId, step });

    editing.flush().catch(() => undefined);

    onClose();
  };

  let body: ReactNode;

  if (step === 'loading' || !draft) {
    body = query.isError ? (
      <VStack className="items-center gap-3 py-10">
        <Text className="text-typography-900">{REPLY_DRAFT_COPY.loadFailed}</Text>
        <Button
          variant="outline"
          action="secondary"
          className="min-h-12"
          onPress={() => {
            query.refetch().catch(() => undefined);
          }}>
          <ButtonText>{REPLY_DRAFT_COPY.retry}</ButtonText>
        </Button>
      </VStack>
    ) : (
      <Center className="py-10" accessibilityLiveRegion="polite">
        <Spinner size="large" accessibilityLabel={REPLY_DRAFT_COPY.loading} />
      </Center>
    );
  } else {
    let viewBody: ReactNode;

    if (step === 'card') {
      viewBody = (
        <DecideStep
          draft={draft}
          isSelecting={editing.busy === 'select'}
          onSelect={select}
          onKeep={() => setRequested('review')}
          headingRef={headingRef}
        />
      );
    } else if (handOffRequest.phase.kind === 'must_fix') {
      const { phase } = handOffRequest;

      viewBody = (
        <MustFixList
          items={phase.items}
          composedHash={phase.composedHash}
          onAdd={(segmentIds) =>
            segmentIds.forEach((segmentId) => editing.tap({ type: 'segment_restored', segmentId }))
          }
          onContinue={continueAfterMustFix}
          onBack={handOffRequest.dismiss}
          headingRef={headingRef}
        />
      );
    } else {
      viewBody = (
        <DraftPoints
          draft={draft}
          isAddingNote={editing.busy === 'note'}
          onTap={editing.tap}
          onAddNote={addNote}
          noteText={noteText}
          onNoteTextChange={setNoteText}
          headingRef={headingRef}
        />
      );
    }

    body = (
      <VStack className="gap-4">
        <VStack className={viewingSource ? 'hidden' : 'gap-4'}>
          <VStack>
            <ReplyDraftTourZone segment="decide" zone={REPLY_DRAFT_TOUR_ZONES.decide.steps}>
              <WizardSteps steps={REPLY_DRAFT_STEPS} current={step} />
            </ReplyDraftTourZone>

            <ReplyDraftTourReplay step={step} isOffered={!isHeldByMustFix} />
          </VStack>

          {draft.askerFollowedUpAt ? (
            <CaseUpdatedNotice askerFollowedUpAt={draft.askerFollowedUpAt} />
          ) : null}

          <VStack key={step}>{viewBody}</VStack>
        </VStack>

        {viewingSource ? <SourceDetail source={viewingSource} /> : null}
      </VStack>
    );
  }

  const forward = step === 'loading' ? null : nextStep(step);

  const back = step === 'loading' ? null : previousStep(step);

  const goBack = back ? () => setRequested(back) : undefined;

  let footer: ReactNode = null;

  if (viewingSource) {
    footer = (
      <DraftFooter>
        <Button
          size="xl"
          variant="outline"
          action="secondary"
          className="min-h-12 w-full"
          onPress={() => setViewingSource(null)}
          testID="reply-draft-source-back">
          <ButtonText className="text-secondary-700">{REPLY_DRAFT_COPY.backToDraft}</ButtonText>
        </Button>
      </DraftFooter>
    );
  } else if (step === 'card' && draft?.draft) {
    footer = (
      <DraftFooter>
        <DraftFooterButton
          label={REPLY_DRAFT_COPY.keepPosition}
          hint={REPLY_DRAFT_COPY.keepPositionHint}
          onPress={() => setRequested('review')}
          testID="reply-draft-keep-position"
        />
      </DraftFooter>
    );
  } else if (step !== 'card' && step !== 'loading' && forward) {
    footer = (
      <DraftFooter onBack={goBack}>
        <DraftFooterButton
          label={REPLY_DRAFT_COPY.next}
          hint={REPLY_DRAFT_COPY.nextHint}
          onPress={() => setRequested(forward)}
          testID="reply-draft-next"
        />
      </DraftFooter>
    );
  } else if (step !== 'card' && step !== 'loading' && !isHeldByMustFix) {
    footer = handOff.pendingReplace ? (
      <ReplaceConfirm
        onReplace={() => {
          handOff.confirmReplace();

          finish();
        }}
        onKeep={() => {
          handOff.declineReplace();

          trackReplyDraftReplaceDeclined({ replyDraftId, conversationId });

          onClose();
        }}
      />
    ) : (
      <ReplyDraftTourZone
        segment="review"
        zone={REPLY_DRAFT_TOUR_ZONES.review.useDraft}
        scrollsIntoView={false}>
        <UseDraftFooter
          phase={handOffRequest.phase}
          isAddingNote={isAddingNoteBeforeHandOff}
          mustFixRemaining={draft?.draft?.pendingMustFix.length ?? 0}
          onUse={() => {
            handOffWithNote().catch(() => undefined);
          }}
          onBack={goBack}
        />
      </ReplyDraftTourZone>
    );
  }

  const title = viewingSource ? REPLY_DRAFT_COPY.sourceTitle : stepTitle;

  return (
    <SourceViewerContext.Provider value={viewSource}>
      <ReplyDraftTourProvider
        step={step}
        isPaused={viewingSource !== null}
        onRevealTarget={revealTourTarget}
        replyDraftId={replyDraftId}
        conversationId={conversationId}>
        <VStack className="h-full w-full flex-1" testID="reply-draft">
          <ReplyDraftHeader title={title} onClose={close} titleRef={titleRef} />

          <ScrollView
            ref={scrollRef}
            className="flex-1"
            contentContainerClassName="px-4"
            contentContainerStyle={{
              paddingTop: headerOverlayHeight + HEAD_FADE_CLEARANCE,
              paddingBottom: (footer ? footerHeight : 0) + BODY_END_SPACE,
            }}
            scrollIndicatorInsets={{
              top: headerOverlayHeight,
              bottom: footer ? footerHeight : 0,
            }}
            onScroll={(event) => {
              scrollOffset.current = event.nativeEvent.contentOffset.y;
            }}
            scrollEventThrottle={64}
            keyboardShouldPersistTaps="handled">
            {body}
          </ScrollView>

          {isWeb && stepAnnouncement ? (
            <Text className="sr-only" accessibilityLiveRegion="polite">
              {stepAnnouncement}
            </Text>
          ) : null}

          {footer ? (
            <View
              pointerEvents="box-none"
              className="absolute bottom-0 left-0 right-0"
              onLayout={(event) => setFooterHeight(event.nativeEvent.layout.height)}>
              <EdgeFade
                edge="bottom"
                height={footerHeight}
                holdHeight={Math.max(0, footerHeight - FOOT_FADE_TAIL)}
              />
              {footer}
            </View>
          ) : null}
        </VStack>

        {bottomSpace}
      </ReplyDraftTourProvider>
    </SourceViewerContext.Provider>
  );
}
